import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
	selectOrder,
	selectOrderDishes,
	selectOrderDishTypes,
	selectOrderState,
} from "slices/transactionSlice";
import { ORDER_STEP_3_SMALL_ORDER_LIMIT } from "utils/designTokens";

import OrderStep2Arrow from "assets/OrderStep2Arrow.svg";
import ChooseFood from "assets/illustrations/chooseFoodImage.svg";
import ProcessingOrderImage from "assets/illustrations/processingOrderImage.svg";
import { BlurredBackground } from "components/primitives/BlurredBackground";
import { DrawerSubHeaderText } from "components/primitives/DrawerSubHeaderText";
import { DrawerToggleIndicator } from "components/primitives/DrawerToggleIndicator";
import { HeaderText } from "components/primitives/HeaderText";
import { StyledBottomMessage } from "components/primitives/StyledBottomMessage";
import { SwipeableArea } from "components/primitives/SwipeableArea";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { useOpenDoorAlarm } from "hooks/useOpenDoorAlarm";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { TransactionState } from "types";
import { LocalDishType } from "types/mainTypes";

import { BaseOrderStepProps } from "./OrderStep2";
import { OpenedDoorWarningBox } from "./components/OpenedDoorWarningBox";
import { OrderDishesList } from "./components/OrderDishesList";
import { StyledDishTypeList } from "./components/StyledDishTypeList";
import { OrderDishesWrapper, OrderItemBlockWrapper } from "./components/orderStepDrawerStyles";

const Body = styled(Box)(() => ({
	width: "100%",
	maxHeight: "1000px",
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	padding: "0 0 120px 0",
	img: {
		marginTop: 16,
	},
}));

const TakeYourMealsHeader = () => {
	const { t } = useTranslation();
	return (
		<>
			<HeaderText variant={"h3"}>{t("order.step3.header")}</HeaderText>
			<OrderStep2Arrow />
			<DrawerSubHeaderText>{t("order.step3.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckHeader = () => {
	const { t } = useTranslation();

	return (
		<>
			<HeaderText variant={"h3"} hasBorder={true}>
				{t("order.step4.header")}
			</HeaderText>
			<DrawerSubHeaderText>{t("order.step4.subheader")}</DrawerSubHeaderText>
		</>
	);
};

const StockCheckDrawerMessage = () => {
	const { t } = useTranslation();
	return (
		<StyledBottomMessage data-testid="stock-check">
			<Typography variant={"promoHeader"}>{t("order.step4.header")}...</Typography>
			<CircularProgress
				sx={{ margin: "auto", color: "#00396c" }}
				size={"75px"}
				thickness={5}
			/>
		</StyledBottomMessage>
	);
};

export const OrderStep3 = ({
	open,
	stock,
	toggleDrawer,
}: BaseOrderStepProps & {
	stock: LocalDishType[];
}) => {
	const orderState = useAppSelector(selectOrderState);
	const order = useAppSelector(selectOrder);
	const isStockCheck = orderState === TransactionState.StockCheck;
	const { trackStockCheckStarted, trackOrderStep3Screen } = useMixpanelTrackers();
	const { resetCountdown, displayOpenedDoorWarning } = useOpenDoorAlarm(order?.type);

	useEffect(() => {
		trackOrderStep3Screen();
	}, []);

	useEffect(() => {
		if (orderState === TransactionState.StockCheck) {
			trackStockCheckStarted();
			resetCountdown();
		}
	}, [orderState]);

	useEffect(() => {
		if (displayOpenedDoorWarning) {
			toggleDrawer(true);
		}
	}, [displayOpenedDoorWarning]);

	return (
		<>
			<SwipeableArea orderstep={3} open={open}>
				{!open && <BlurredBackground orderStep={3} open={open} />}
				<DrawerToggleIndicator open={open} toggleDrawer={toggleDrawer} />
				<Box
					sx={{ width: "100%", display: "flex", flexDirection: "column", paddingTop: 2 }}
				>
					{isStockCheck ? <StockCheckHeader /> : <TakeYourMealsHeader />}
				</Box>
			</SwipeableArea>
			<Body
				sx={{
					...(open && {
						marginTop: "220px",
					}),
				}}
			>
				<OrderStep3Body
					stock={stock}
					displayOpenedDoorWarning={displayOpenedDoorWarning}
					resetCountdown={() => resetCountdown()}
				/>
			</Body>
		</>
	);
};

interface OrderStep3BodyProps {
	stock: LocalDishType[];
	displayOpenedDoorWarning: boolean;
	resetCountdown: () => void;
}

const OrderStep3Body = (props: OrderStep3BodyProps) => {
	const { t } = useTranslation();
	const orderDishes = useAppSelector(selectOrderDishes);
	const orderDishTypes = useAppSelector(selectOrderDishTypes);
	const order = useAppSelector(selectOrder);
	const { displayOpenedDoorWarning, resetCountdown } = props;

	if (orderDishes.length === 0 && order?.state === TransactionState.Active) {
		return (
			<>
				<ChooseFood />
				{displayOpenedDoorWarning && (
					<OpenedDoorWarningBox resetCountdown={resetCountdown} />
				)}
			</>
		);
	}

	if (orderDishes.length !== 0) {
		return (
			<>
				<StyledDishTypeList
					isLargeOrder={
						orderDishTypes && orderDishTypes?.length > ORDER_STEP_3_SMALL_ORDER_LIMIT
					}
					data-testid="dish-list"
				>
					<OrderItemBlockWrapper>
						{orderDishTypes && (
							<OrderDishesWrapper>
								<OrderDishesList smallOrderLimit={ORDER_STEP_3_SMALL_ORDER_LIMIT} />
							</OrderDishesWrapper>
						)}
					</OrderItemBlockWrapper>
				</StyledDishTypeList>
				{order?.state === TransactionState.StockCheck && <StockCheckDrawerMessage />}
				{displayOpenedDoorWarning && (
					<OpenedDoorWarningBox resetCountdown={resetCountdown} />
				)}
			</>
		);
	}

	if (order?.state === TransactionState.StockCheck) {
		return (
			<>
				<ProcessingOrderImage
					style={{
						position: "absolute",
						top: "165px",
					}}
				/>
				<StyledBottomMessage data-testid="stock-check">
					<Typography variant={"promoHeader"}>{t("order.step4.header")}...</Typography>
					<CircularProgress
						sx={{ margin: "auto", color: "#00396c" }}
						size={"75px"}
						thickness={5}
					/>
				</StyledBottomMessage>
			</>
		);
	}
};
